import React from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useTheme, useBrand } from '../context/ColorContext';
import { Settings, ShieldCheck, Factory, Zap, Target, Leaf } from 'lucide-react';

const DEFAULT_URETIM = {
    heroTitle: "Üretim Tesislerimiz",
    heroSubtitle: "Türkiye'de toplam 72 bin m² alan üzerine kurulu, son teknolojilerle donatılmış modern üretim tesislerimizde güvenilir bir üretim ortamı sağlıyoruz.",
    stats: [
        { value: "142.000", label: "Ton / Yıl Üretim Kapasitesi" },
        { value: "72.000", label: "m² Üretim Alanı" },
        { value: "TOP 5", label: "Avrupa'nın En Büyükleri" },
        { value: "#73", label: "Coatings World 2024" }
    ],
    section1: {
        title: "Avrupa'nın Lider Üreticilerinden",
        p1: "142 bin tonluk üretim kapasitesi ile Avrupa'nın 5 büyük boya ve vernik üreticisi konumunda olan Kayalar Kimya, sektörünün dünya çapında en prestijli şirketlerini belirleyen Coatings World listesine 2024 yılında 73. sıradan girmiştir.",
        p2: "Yenilikçi ürün ve hizmetler geliştirmeye odaklı, ufku dünya pazarlarına açık bir misyon ile mobilya boyaları, dekoratif boyalar, endüstriyel boyalar ve ahşap koruyucular üretmekteyiz.",
        image: "https://images.unsplash.com/photo-1565514020179-026b92b84bb6?auto=format&fit=crop&q=80&w=800"
    },
    section2: {
        title: "Çevre Dostu ve Sürdürülebilir",
        p1: "Üretim süreçlerimizin merkezinde sürdürülebilirlik ve doğaya saygı yer almaktadır. Kapalı devre su sirkülasyon sistemlerimiz ve baca filtreleme üniteleriyle atık salınımını en düşük seviyede tutuyoruz.",
        p2: "Su bazlı ve çevre dostu ürün gamımızı sürekli genişleterek, hem insan sağlığına hem de çevreye duyarlı üretim anlayışımızı her geçen gün güçlendiriyoruz."
    },
    section3: {
        tag: "Yenilikçilik",
        title: "AR-GE ve Kalite Kontrol",
        desc: "Bakanlık onaylı AR-GE Merkezimizde, Uygulama ve Simülasyon Merkezimizde ve Teknik Servis Eğitim Merkezimizde dünya standartlarında araştırma geliştirme faaliyetleri yürütmekteyiz.",
        features: ["AR-GE Merkezi", "Teknik Servis ve Eğitim", "Uygulama ve Simülasyon"]
    }
};

const fetchUretim = async () => {
    const res = await axios.get('/api/pages/uretim');
    return res.data;
};

const Uretim = () => {
    const { theme } = useTheme();
    const { activeBrand } = useBrand();
    const isDark = theme === 'dark';

    const { data: pageData, isLoading } = useQuery({
        queryKey: ['page_uretim'],
        queryFn: fetchUretim,
        retry: false
    });

    const content = pageData || DEFAULT_URETIM;

    const stats = [
        { icon: <Factory size={24} />, value: content.stats?.[0]?.value || '1.000.000+', label: content.stats?.[0]?.label || 'Günlük Üretim Hacmi' },
        { icon: <ShieldCheck size={24} />, value: content.stats?.[1]?.value || 'ISO 9001', label: content.stats?.[1]?.label || 'Kalite Standardı' },
        { icon: <Settings size={24} />, value: content.stats?.[2]?.value || '24/7', label: content.stats?.[2]?.label || 'Otomasyon Sistemi' },
        { icon: <Target size={24} />, value: content.stats?.[3]?.value || 'TSE & CE', label: content.stats?.[3]?.label || 'Sertifikasyonlar' },
    ];

    const mascotImages = {
        'dufa': '/img/dufa-kirpi-transparan.png',
        'genc': '/img/genc-maskot.png',
        'woodsol': '/img/woodsol-maskot.png'
    };

    if (isLoading) {
        return <div className="min-h-screen flex items-center justify-center">Yükleniyor...</div>;
    }

    return (
        <div className={`min-h-screen pt-32 pb-20 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            <div className="container mx-auto px-4 md:px-0 text-center mb-24">
                <span className="text-sm font-bold tracking-[0.4em] opacity-60 uppercase mb-4 block">Kayalar Kimya & {activeBrand.name}</span>
                <h1 className="text-5xl md:text-8xl font-black uppercase mb-8 tracking-tighter leading-none">{content.heroTitle}</h1>
                <p className="max-w-3xl mx-auto text-xl md:text-2xl font-light leading-relaxed opacity-80">
                    {content.heroSubtitle}
                </p>
            </div>

            {/* Stats Grid */}
            <div className="container mx-auto px-4 md:px-0 mb-32">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-10">
                    {stats.map((stat, idx) => (
                        <div key={idx} className={`p-8 rounded-[2.5rem] border ${isDark ? 'bg-white/5 border-white/10' : 'bg-black/5 border-black/5'} backdrop-blur-xl flex flex-col items-center text-center transition-transform hover:scale-105`}>
                            <div className="mb-4" style={{ color: activeBrand.primaryColor }}>{stat.icon}</div>
                            <div className="text-3xl md:text-5xl font-black mb-2">{stat.value}</div>
                            <div className="text-xs font-bold uppercase tracking-widest opacity-60">{stat.label}</div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Core Content - Alternating Sections */}
            <div className="container mx-auto px-4 md:px-0 space-y-32">

                {/* Section 1: Technology */}
                <div className="flex flex-col lg:flex-row items-center gap-16">
                    <div className="lg:w-1/2">
                        <h2 className="text-4xl md:text-6xl font-black uppercase mb-8 tracking-tighter">{content.section1?.title}</h2>
                        <div className="space-y-6 text-lg font-light opacity-75 leading-relaxed">
                            <p>{content.section1?.p1}</p>
                            <p>{content.section1?.p2}</p>
                        </div>
                    </div>
                    <div className="lg:w-1/2 relative">
                        <div className={`aspect-square rounded-[3rem] overflow-hidden border ${isDark ? 'border-white/10' : 'border-black/5'} relative group`}>
                            <img src={content.section1?.image || "https://images.unsplash.com/photo-1565514020179-026b92b84bb6?auto=format&fit=crop&q=80&w=800"} alt="Tesis" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 grayscale-[30%]" />
                            <div className="absolute inset-0 opacity-20 mix-blend-overlay" style={{ backgroundColor: activeBrand.primaryColor }}></div>
                        </div>
                        {/* Floating Decoration */}
                        <div className="absolute -bottom-10 -right-10 w-48 h-48 rounded-full blur-[100px] opacity-20" style={{ backgroundColor: activeBrand.primaryColor }}></div>
                    </div>
                </div>

                {/* Section 2: Environment */}
                <div className="flex flex-col lg:flex-row-reverse items-center gap-16">
                    <div className="lg:w-1/2">
                        <h2 className="text-4xl md:text-6xl font-black uppercase mb-8 tracking-tighter">{content.section2?.title}</h2>
                        <div className="space-y-6 text-lg font-light opacity-75 leading-relaxed">
                            <p>{content.section2?.p1}</p>
                            <p>{content.section2?.p2}</p>
                        </div>
                    </div>
                    <div className="lg:w-1/2">
                        <div className={`p-12 rounded-[3rem] border ${isDark ? 'bg-white/5 border-white/10' : 'bg-black/5 border-black/5'} backdrop-blur-3xl relative overflow-hidden group`}>
                            <div className="flex flex-col gap-8 relative z-10">
                                <div className="flex items-center gap-6">
                                    <div className="w-16 h-16 rounded-2xl bg-[#5AA668]/10 flex items-center justify-center p-2 shadow-xl">
                                        <Leaf size={32} className="text-[#5AA668]" />
                                    </div>
                                    <div className="h-0.5 w-12" style={{ backgroundColor: '#5AA668' }}></div>
                                    <div className="text-2xl font-black uppercase tracking-tighter text-[#5AA668]">DOĞA DOSTU</div>
                                </div>
                                <h3 className="text-3xl font-black uppercase">Tam Geri Dönüşüm</h3>
                                <p className="opacity-70">Su sirkülasyon sistemlerimiz ve baca filtrelemelerimiz ile atık salınımını en düşük seviyede tutuyoruz.</p>
                            </div>
                            <div className="absolute top-0 right-0 w-64 h-64 rounded-full blur-[120px] opacity-10 group-hover:opacity-20 transition-opacity bg-[#5AA668]"></div>
                        </div>
                    </div>
                </div>

                {/* Section 3: R&D */}
                <div className="rounded-[4rem] p-12 md:p-24 relative overflow-hidden text-white group" style={{ backgroundColor: activeBrand.primaryColor }}>
                    <div className="relative z-10 max-w-4xl">
                        <span className="text-xs font-bold tracking-[0.4em] uppercase opacity-70 mb-4 block">{content.section3?.tag}</span>
                        <h2 className="text-4xl md:text-7xl font-black uppercase mb-8 leading-none tracking-tighter">{content.section3?.title}</h2>
                        <p className="text-xl md:text-2xl font-light leading-relaxed mb-12 opacity-90">
                            {content.section3?.desc}
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-white/20 rounded-xl backdrop-blur-md">
                                    <Zap size={20} />
                                </div>
                                <span className="font-bold uppercase tracking-widest text-sm">{content.section3?.features?.[0]}</span>
                            </div>
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-white/20 rounded-xl backdrop-blur-md">
                                    <ShieldCheck size={20} />
                                </div>
                                <span className="font-bold uppercase tracking-widest text-sm">{content.section3?.features?.[1]}</span>
                            </div>
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-white/20 rounded-xl backdrop-blur-md">
                                    <Target size={20} />
                                </div>
                                <span className="font-bold uppercase tracking-widest text-sm">{content.section3?.features?.[2]}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Uretim;
